import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { AppSubProvider, useAppSub } from "@/context/AppSubContext";
import AppShell from "@/components/AppShell";
import AuthPage from "@/pages/Auth";
import Home from "@/pages/Home";
import Friends from "@/pages/Friends";
import SendPhoto from "@/pages/SendPhoto";
import Wallet from "@/pages/Wallet";
import Profile from "@/pages/Profile";
import Conversations from "@/pages/Conversations";
import Conversation from "@/pages/Conversation";
import PaymentResult from "@/pages/PaymentResult";
import Stats from "@/pages/Stats";
import CreatorWall from "@/pages/CreatorWall";
import CreatorPublic from "@/pages/CreatorPublic";
import Admin from "@/pages/Admin";
import Feed from "@/pages/Feed";
import Favorites from "@/pages/Favorites";
import SoundPage from "@/pages/Sound";
import Explore from "@/pages/Explore";
import CreateStudio from "@/pages/CreateStudio";
import AppSubscribe from "@/pages/AppSubscribe";
import FollowList from "@/pages/FollowList";
import { AboutPage, PricingPage, PaymentsPage, RefundPage, TermsPage, PrivacyPage, ContactPage } from "@/pages/Legal";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground">
    Chargement…
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const { sub, loading: subLoading } = useAppSub();
  const location = useLocation();
  if (loading || subLoading) return <FullLoader />;
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  // App-level paywall gate: must have an active app subscription (or free trial) to access.
  if (!sub || !sub.is_active) return <Navigate to="/abonnement" replace />;
  return <AppShell>{children}</AppShell>;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Auth-only wrapper WITHOUT the app shell (immersive full-screen pages like the studio).
const ProtectedBare = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <AppSubProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/auth" element={<PublicOnly><AuthPage /></PublicOnly>} />
              {/* Pages publiques (vérification Stripe / informations légales) — accessibles sans compte */}
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/tarifs" element={<PricingPage />} />
              <Route path="/paiements" element={<PaymentsPage />} />
              <Route path="/remboursement" element={<RefundPage />} />
              <Route path="/cgu" element={<TermsPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              {/* Public creator showcase page (no auth required to view teasers) */}
              <Route path="/c/:username" element={<CreatorPublic />} />
              <Route path="/" element={<Protected><Home /></Protected>} />
              <Route path="/amis" element={<Protected><Friends /></Protected>} />
              <Route path="/envoyer" element={<Protected><SendPhoto /></Protected>} />
              <Route path="/mur" element={<Protected><CreatorWall /></Protected>} />
              <Route path="/messages" element={<Protected><Conversations /></Protected>} />
              <Route path="/messages/:username" element={<Protected><Conversation /></Protected>} />
              <Route path="/portefeuille" element={<Protected><Wallet /></Protected>} />
              <Route path="/statistiques" element={<Protected><Stats /></Protected>} />
              <Route path="/admin" element={<Protected><Admin /></Protected>} />
              <Route path="/decouvrir" element={<Protected><Feed /></Protected>} />
              <Route path="/favoris" element={<Protected><Favorites /></Protected>} />
              <Route path="/son/:key" element={<Protected><SoundPage /></Protected>} />
              <Route path="/explorer" element={<Protected><Explore /></Protected>} />
              <Route path="/creer" element={<ProtectedBare><CreateStudio /></ProtectedBare>} />
              <Route path="/abonnement" element={<ProtectedBare><AppSubscribe /></ProtectedBare>} />
              <Route path="/abonnes" element={<Protected><FollowList mode="followers" /></Protected>} />
              <Route path="/abonnements" element={<Protected><FollowList mode="following" /></Protected>} />
              <Route path="/payment/success" element={<ProtectedBare><PaymentResult /></ProtectedBare>} />
              <Route path="/payment/cancel" element={<ProtectedBare><PaymentResult /></ProtectedBare>} />
              <Route path="/subscription/success" element={<ProtectedBare><PaymentResult /></ProtectedBare>} />
              <Route path="/profil" element={<Protected><Profile /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </AppSubProvider>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
